import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, ClipboardList, Check, Ban } from "lucide-react";
import {
  useStockCounts,
  useStockCount,
  useCreateStockCount,
  useUpdateStockCountItem,
  useApplyStockCount,
  useCancelStockCount,
  useCategories,
  useBranches,
  PRODUCT_UNIT_LABEL,
  StockCountSummary,
  StockCountItemRecord,
} from "@/api/inventory";
import { getApiErrorMessage } from "@/api/client";

const STATUS_LABEL: Record<StockCountSummary["status"], string> = {
  ABIERTA: "Abierta",
  APLICADA: "Aplicada",
  CANCELADA: "Cancelada",
};

const STATUS_BADGE: Record<StockCountSummary["status"], string> = {
  ABIERTA: "bg-amber-100 text-amber-700",
  APLICADA: "bg-green-100 text-green-700",
  CANCELADA: "bg-gray-100 text-gray-500",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Toma de inventario (conteo físico) — segunda pieza de "Inventario
 * avanzado". Una toma se abre para todos los productos activos o filtrada
 * por categoría, se registra la cantidad contada producto por producto, y al
 * aplicarla se ajusta el stock de una sola vez por cada producto con
 * diferencia (la diferencia se calcula contra el saldo del sistema en el
 * momento en que se agregó la línea, no contra el saldo actual, para que no
 * cambie mientras se está contando).
 */
export default function StockCountsPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [branchFilter, setBranchFilter] = useState<string>("");
  const { data: branches } = useBranches();
  const { data: stockCounts, isLoading } = useStockCounts(branchFilter || undefined);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Toma de inventario</h1>
          <p className="text-sm text-gray-500">Registre el conteo físico de sus productos y aplique los ajustes de una sola vez.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nueva toma
        </button>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select className="input-field w-auto" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
          <option value="">Todas las sucursales</option>
          {branches?.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Fecha</th>
              <th className="px-4 py-2.5">Sucursal</th>
              <th className="px-4 py-2.5">Estado</th>
              <th className="px-4 py-2.5">Productos</th>
              <th className="px-4 py-2.5">Contados</th>
              <th className="px-4 py-2.5">Con diferencia</th>
              <th className="px-4 py-2.5">Notas</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {stockCounts?.map((c) => (
              <tr key={c.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setOpenId(c.id)}>
                <td className="px-4 py-2.5 text-gray-700">{formatDate(c.createdAt)}</td>
                <td className="px-4 py-2.5 text-gray-700">{c.branchName ?? "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                </td>
                <td className="px-4 py-2.5 text-gray-700">{c.itemCount}</td>
                <td className="px-4 py-2.5 text-gray-700">{c.countedCount}</td>
                <td className="px-4 py-2.5">
                  {c.itemsWithVarianceCount > 0 ? <span className="font-medium text-amber-600">{c.itemsWithVarianceCount}</span> : <span className="text-gray-400">0</span>}
                </td>
                <td className="px-4 py-2.5 text-gray-500">{c.notes ?? "—"}</td>
                <td className="px-4 py-2.5 text-right text-primary-700">Ver</td>
              </tr>
            ))}
            {stockCounts?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  Todavía no hay tomas de inventario registradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateStockCountModal onClose={() => setShowCreate(false)} onCreated={(id) => setOpenId(id)} />}
      {openId && <StockCountDetailModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function CreateStockCountModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [notes, setNotes] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [branchId, setBranchId] = useState("");
  const { data: categories } = useCategories(true);
  const { data: branches } = useBranches(true);
  const createStockCount = useCreateStockCount();

  useEffect(() => {
    if (!branchId && branches && branches.length > 0) {
      setBranchId(branches.find((b) => b.isMain)?.id ?? branches[0].id);
    }
  }, [branches, branchId]);

  const handleCreate = async () => {
    if (!branchId) {
      toast.error("Elige la sucursal a contar.");
      return;
    }
    try {
      const created = await createStockCount.mutateAsync({ branchId, notes: notes || undefined, categoryId: categoryId || undefined });
      toast.success("Toma de inventario creada.");
      onClose();
      onCreated(created.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Nueva toma de inventario</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4 p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Sucursal</label>
            <select className="input-field" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Seleccione...</option>
              {branches?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Categoría (opcional)</label>
            <select className="input-field" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Todos los productos activos</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-400">Si no elige una categoría, se incluyen todos los productos activos del catálogo.</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Notas (opcional)</label>
            <textarea className="input-field" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={createStockCount.isPending} onClick={handleCreate}>
            {createStockCount.isPending ? "Creando..." : "Crear toma"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StockCountDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: stockCount, isLoading } = useStockCount(id);
  const updateItem = useUpdateStockCountItem(id);
  const applyStockCount = useApplyStockCount();
  const cancelStockCount = useCancelStockCount();
  const [draftQty, setDraftQty] = useState<Record<string, string>>({});

  const isOpen = stockCount?.status === "ABIERTA";

  const handleSaveCount = async (item: StockCountItemRecord) => {
    const raw = draftQty[item.id];
    if (raw === undefined) return;
    const qty = raw.trim() === "" ? null : Number(raw);
    if (qty !== null && (Number.isNaN(qty) || qty < 0)) {
      toast.error("Cantidad inválida.");
      return;
    }
    try {
      await updateItem.mutateAsync({ itemId: item.id, countedQty: qty });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleApply = async () => {
    if (!window.confirm("¿Aplicar esta toma de inventario? Se ajustará el stock de los productos con diferencia.")) return;
    try {
      await applyStockCount.mutateAsync(id);
      toast.success("Toma de inventario aplicada. El stock fue ajustado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Cancelar esta toma de inventario? No se hará ningún ajuste de stock.")) return;
    try {
      await cancelStockCount.mutateAsync(id);
      toast.success("Toma de inventario cancelada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              <ClipboardList size={16} /> Detalle de la toma de inventario
            </h2>
            {stockCount && (
              <p className="mt-1 flex items-center gap-2">
                <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[stockCount.status]}`}>{STATUS_LABEL[stockCount.status]}</span>
                {stockCount.branch?.name && <span className="text-xs text-gray-400">{stockCount.branch.name}</span>}
              </p>
            )}
          </div>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {stockCount?.notes && <p className="mb-3 text-sm text-gray-500">Notas: {stockCount.notes}</p>}
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-2 py-2">Producto</th>
                <th className="px-2 py-2">Sistema</th>
                <th className="px-2 py-2">Contado</th>
                <th className="px-2 py-2">Diferencia</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stockCount?.items.map((item) => {
                const counted = draftQty[item.id] !== undefined ? draftQty[item.id] : item.countedQty ?? "";
                const countedNum = counted.trim() === "" ? null : Number(counted);
                const variance = countedNum !== null && !Number.isNaN(countedNum) ? countedNum - Number(item.systemQty) : null;
                return (
                  <tr key={item.id}>
                    <td className="px-2 py-2 text-gray-700">
                      {item.product?.name} <span className="text-gray-400">({item.product?.sku})</span>
                    </td>
                    <td className="px-2 py-2 text-gray-500">
                      {item.systemQty} {item.product ? PRODUCT_UNIT_LABEL[item.product.unit] : ""}
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        step="0.001"
                        className="input-field w-28"
                        disabled={!isOpen}
                        value={counted}
                        onChange={(e) => setDraftQty((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        onBlur={() => handleSaveCount(item)}
                      />
                    </td>
                    <td className="px-2 py-2">
                      {variance === null ? (
                        <span className="text-gray-400">—</span>
                      ) : variance === 0 ? (
                        <span className="text-green-600">0</span>
                      ) : (
                        <span className={variance > 0 ? "text-blue-600" : "text-red-600"}>{variance > 0 ? `+${variance}` : variance}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {stockCount?.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-2 py-6 text-center text-gray-400">
                    Esta toma no tiene productos.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {isOpen && (
          <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
            <button type="button" className="btn-secondary" onClick={handleCancel} disabled={cancelStockCount.isPending}>
              <Ban size={16} className="mr-1.5" /> Cancelar toma
            </button>
            <button type="button" className="btn-primary" onClick={handleApply} disabled={applyStockCount.isPending}>
              <Check size={16} className="mr-1.5" /> Aplicar ajustes de stock
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
